import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  emoji: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    quote:
      "My son went from taking 15 minutes on mental math homework to 2 minutes in just 3 weeks. He actually asks to practice now!",
    author: "Sarah M.",
    role: "Parent of 4th grader",
    emoji: "📈",
    rating: 5,
  },
  {
    quote:
      "Ithopu transformed my classroom. Students compete for high scores, and I can see exactly who needs help. Setup took 5 minutes.",
    author: "Mr. Johnson",
    role: "5th Grade Math Teacher",
    emoji: "🎯",
    rating: 5,
  },
  {
    quote:
      "I used to hate math. Now I'm the fastest in my class at multiplication! The games and tournaments make it actually fun.",
    author: "Priya K.",
    role: "6th Grade Student",
    emoji: "🏆",
    rating: 5,
  },
  {
    quote:
      "As a parent, the weekly progress reports are amazing. I can see exactly what my daughter is working on and where she's improving.",
    author: "David L.",
    role: "Parent of 3rd grader",
    emoji: "📊",
    rating: 5,
  },
  {
    quote:
      "My daughter scored in the top 5% at the Math Olympiad after just 2 months of using Ithopu. The techniques really work!",
    author: "Anita P.",
    role: "Parent of 7th grader",
    emoji: "🥇",
    rating: 5,
  },
];

export default function TestimonialsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative max-w-4xl mx-auto">
      {/* Main Testimonial Card */}
      <Card className="border-2 border-blue-200 dark:border-blue-800 overflow-hidden">
        <CardContent className="pt-8 pb-8 px-8">
          <div className="text-center">
            <div className="text-6xl mb-4">{currentTestimonial.emoji}</div>

            {/* Star Rating */}
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(currentTestimonial.rating)].map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 text-yellow-500 fill-yellow-500"
                />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="text-lg md:text-xl italic text-zinc-700 dark:text-zinc-300 mb-6 min-h-[120px] flex items-center justify-center">
              "{currentTestimonial.quote}"
            </blockquote>

            {/* Author */}
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {currentTestimonial.author}
            </p>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {currentTestimonial.role}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 bg-white dark:bg-zinc-800 rounded-full p-2 shadow-lg hover:shadow-xl transition-all border-2 border-zinc-200 dark:border-zinc-700 hover:scale-110"
        aria-label="Previous testimonial"
      >
        <ChevronLeft className="w-6 h-6 text-zinc-700 dark:text-zinc-300" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 bg-white dark:bg-zinc-800 rounded-full p-2 shadow-lg hover:shadow-xl transition-all border-2 border-zinc-200 dark:border-zinc-700 hover:scale-110"
        aria-label="Next testimonial"
      >
        <ChevronRight className="w-6 h-6 text-zinc-700 dark:text-zinc-300" />
      </button>

      {/* Dot Indicators */}
      <div className="flex items-center justify-center gap-2 mt-6">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all rounded-full ${
              index === currentIndex
                ? "w-8 h-2 bg-blue-600 dark:bg-blue-400"
                : "w-2 h-2 bg-zinc-300 dark:bg-zinc-600 hover:bg-zinc-400 dark:hover:bg-zinc-500"
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>

      {/* Auto-play indicator */}
      <div className="text-center mt-4">
        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
        >
          {isAutoPlaying ? "⏸ Pause auto-play" : "▶ Resume auto-play"}
        </button>
      </div>
    </div>
  );
}
